import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import PortalLayout from "@/components/ventrix/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, ArrowUp, ArrowDown, Eye, EyeOff } from "lucide-react";
import { SERVICE_ICON_NAMES, iconFor } from "@/lib/serviceIcons";

const blank = () => ({
  title: "", slug: "", tagline: "", icon: "Package", image: "",
  bullets: [], stats: [], tag: "", eta: "", card_desc: "", wide: false, active: true,
});

// bullets <-> textarea (one per line); stats <-> "value | label" per line.
const bulletsToText = (b) => (b || []).join("\n");
const textToBullets = (t) => t.split("\n").map((x) => x.trim()).filter(Boolean);
const statsToText = (s) => (s || []).map(([v, l]) => `${v} | ${l}`).join("\n");
const textToStats = (t) => t.split("\n").map((line) => line.split("|").map((x) => x.trim()))
  .filter((pair) => pair[0]).map((pair) => [pair[0], pair[1] || ""]);

export default function Services() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(blank());

  const load = () => {
    setItems(null);
    api.get("/v1/admin/services")
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]));
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setF(blank()); setOpen(true); };
  const startEdit = (s) => { setEditing(s); setF({ ...blank(), ...s }); setOpen(true); };

  const save = async () => {
    if (!f.title.trim()) { toast.error("Title is required"); return; }
    const payload = {
      title: f.title, slug: f.slug || undefined, tagline: f.tagline, icon: f.icon,
      image: f.image, bullets: f.bullets, stats: f.stats, tag: f.tag, eta: f.eta,
      card_desc: f.card_desc, wide: f.wide, active: f.active,
    };
    try {
      if (editing) await api.patch(`/v1/admin/services/${editing.id}`, payload);
      else await api.post("/v1/admin/services", payload);
      setOpen(false); load(); toast.success("Service saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete "${s.title}"? This removes it from the public site.`)) return;
    try { await api.delete(`/v1/admin/services/${s.id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleActive = async (s) => {
    try { await api.patch(`/v1/admin/services/${s.id}`, { active: !s.active }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const move = async (idx, dir) => {
    if (!items) return;
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const reordered = [...items];
    [reordered[idx], reordered[j]] = [reordered[j], reordered[idx]];
    setItems(reordered); // optimistic
    try { await api.put("/v1/admin/services/reorder", { ids: reordered.map((x) => x.id) }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); load(); }
  };

  return (
    <PortalLayout section="admin">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="vtx-eyebrow">/ Admin · Services</div>
          <h1 className="mt-2 text-3xl font-black text-[#0E2F76] tracking-tighter">Landing-page services</h1>
        </div>
        <button className="vtx-btn-primary inline-flex items-center gap-2" onClick={startCreate} data-testid="new-service"><Plus size={14} /> New service</button>
      </div>

      <div className="vtx-card">
        {items === null && <div className="p-6 space-y-2">{[1, 2, 3].map((i) => <div key={i} className="vtx-skeleton h-12" />)}</div>}
        {items && (
          <div className="overflow-x-auto">
            <table className="vtx-table w-full">
              <thead><tr><th>Order</th><th>Service</th><th>Slug</th><th>Tag</th><th>Active</th><th></th></tr></thead>
              <tbody>
                {items.length === 0 && <tr><td colSpan="6" className="text-center text-sm text-[#475569] py-10">No services yet.</td></tr>}
                {items.map((s, i) => {
                  const Icon = iconFor(s.icon);
                  return (
                    <tr key={s.id} data-testid={`service-row-${s.slug}`}>
                      <td>
                        <div className="flex items-center gap-1">
                          <button disabled={i === 0} onClick={() => move(i, -1)} className="text-[#0E2F76] disabled:opacity-30" data-testid={`svc-up-${s.slug}`}><ArrowUp size={14} /></button>
                          <button disabled={i === items.length - 1} onClick={() => move(i, 1)} className="text-[#0E2F76] disabled:opacity-30" data-testid={`svc-down-${s.slug}`}><ArrowDown size={14} /></button>
                        </div>
                      </td>
                      <td className="font-semibold text-[#0E2F76] inline-flex items-center gap-2"><Icon size={15} className="text-[#0E2F76]" /> {s.title}</td>
                      <td className="font-mono text-xs text-[#475569]">{s.slug}</td>
                      <td className="text-xs">{s.tag || "—"}</td>
                      <td>
                        <button onClick={() => toggleActive(s)} data-testid={`svc-active-${s.slug}`} className="inline-flex items-center gap-1 text-xs font-bold">
                          {s.active ? <><Eye size={13} className="text-[#166534]" /> <span className="text-[#166534]">ON</span></> : <><EyeOff size={13} className="text-[#475569]" /> <span className="text-[#475569]">off</span></>}
                        </button>
                      </td>
                      <td className="flex gap-2">
                        <button onClick={() => startEdit(s)} data-testid={`edit-service-${s.slug}`} className="text-[#0E2F76] hover:text-[#d41f26]"><Pencil size={14} /></button>
                        <button onClick={() => del(s)} data-testid={`del-service-${s.slug}`} className="text-[#d41f26]"><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} service</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <label><span className="vtx-eyebrow block mb-1.5">Title</span><input className="vtx-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="svc-title" /></label>
            <label><span className="vtx-eyebrow block mb-1.5">Slug {editing && <span className="text-[#d41f26] normal-case">(changing breaks existing links)</span>}</span><input className="vtx-input font-mono" placeholder="auto from title" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} data-testid="svc-slug" /></label>
            <label className="col-span-2"><span className="vtx-eyebrow block mb-1.5">Tagline (detail hero)</span><input className="vtx-input" value={f.tagline} onChange={(e) => setF({ ...f, tagline: e.target.value })} /></label>
            <label><span className="vtx-eyebrow block mb-1.5">Icon</span>
              <select className="vtx-input" value={f.icon} onChange={(e) => setF({ ...f, icon: e.target.value })} data-testid="svc-icon">
                {SERVICE_ICON_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <label><span className="vtx-eyebrow block mb-1.5">Image URL</span><input className="vtx-input" value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} /></label>
            <label><span className="vtx-eyebrow block mb-1.5">Card tag</span><input className="vtx-input" value={f.tag} onChange={(e) => setF({ ...f, tag: e.target.value })} placeholder="e.g. Fastest" /></label>
            <label><span className="vtx-eyebrow block mb-1.5">Card ETA</span><input className="vtx-input" value={f.eta} onChange={(e) => setF({ ...f, eta: e.target.value })} placeholder="e.g. 1–5 days" /></label>
            <label className="col-span-2"><span className="vtx-eyebrow block mb-1.5">Card description</span><input className="vtx-input" value={f.card_desc} onChange={(e) => setF({ ...f, card_desc: e.target.value })} /></label>
            <label className="col-span-2"><span className="vtx-eyebrow block mb-1.5">Bullets (one per line)</span><textarea rows={4} className="vtx-input" value={bulletsToText(f.bullets)} onChange={(e) => setF({ ...f, bullets: textToBullets(e.target.value) })} data-testid="svc-bullets" /></label>
            <label className="col-span-2"><span className="vtx-eyebrow block mb-1.5">Stats (one per line: <span className="font-mono">value | label</span>)</span><textarea rows={3} className="vtx-input font-mono text-xs" value={statsToText(f.stats)} onChange={(e) => setF({ ...f, stats: textToStats(e.target.value) })} data-testid="svc-stats" /></label>
            <label className="flex items-center gap-2 mt-1"><input type="checkbox" checked={f.wide} onChange={(e) => setF({ ...f, wide: e.target.checked })} /> <span className="text-sm font-semibold">Wide card</span></label>
            <label className="flex items-center gap-2 mt-1"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> <span className="text-sm font-semibold">Active (visible on site)</span></label>
          </div>
          <DialogFooter>
            <button className="vtx-btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
            <button className="vtx-btn-primary" onClick={save} data-testid="save-service">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PortalLayout>
  );
}
